import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'constants/AppRoutes';
import { COMPANY_MAP_TABS } from 'constants/interfaces';
import { useAiScreenActions } from 'contexts/AiScreenActionsContext';
import { useAppDispatch } from 'store/hooks/useApp';
import { setValueChainMapState } from 'store/slices/valueChainMap/slice';

import type { IOpenAgentSessionAction } from 'utils/interfaces/IAiScreenAction';

/** What the caller says: which session of which map, and which agent. The type is implied. */
export type TOpenAgentSession = Omit<IOpenAgentSessionAction, 'type'>;

/**
 * Opens an N-tier discovery session in the state it is in, from wherever the user is.
 *
 * The same route as `useOpenImpactAnalysis`: the map's value chain tab answers the action when it
 * is mounted; when it is not, the action waits and the app navigates to the map's value chain
 * tab, which takes the action as it mounts.
 */
const useOpenAgentSession = (): (request: TOpenAgentSession) => void => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dispatch: dispatchScreenAction } = useAiScreenActions();

  return useCallback((request: TOpenAgentSession) => {
    if (dispatchScreenAction({ type: 'openAgentSession', ...request })) {
      return;
    }

    dispatch(setValueChainMapState({ currentCompanyMapTab: COMPANY_MAP_TABS.VALUE_CHAIN_MAP }));
    navigate(AppRoutes.companyMap.replace(':valueChainMapId', `${request.valueChainMapId}`));
  }, [dispatch, dispatchScreenAction, navigate]);
};

export default useOpenAgentSession;
